import React from "react";
import GuidePanel from "../GuidePanel.js";
import {
  GuideLead,
  GuideCallout,
  GuideSectionLabel,
  GuideCard,
  GuideCardGrid,
  GuideBlockList,
} from "../GuideBlocks.js";

const ClearDecisionMakingSection = ({ heading, accentIndex = 0, getVisual, getAccent }) => (
  <GuidePanel
    sectionId="clear-decision-making"
    heading={heading}
    accentIndex={accentIndex}
    getVisual={getVisual}
    getAccent={getAccent}
  >
    <GuideLead>
      Clarity does not usually arrive before a decision. It arrives from a process. If you are
      waiting to feel clear before you decide, you will likely wait through the exact window where a
      decision would have helped most.
    </GuideLead>
    <p className="guide-panel__p">
      <strong>Clear decision making</strong> is a repeatable process, not a mental state you either
      have or do not.
    </p>
    <GuideSectionLabel>Why decisions feel foggy</GuideSectionLabel>
    <p className="guide-panel__p">
      Most foggy decisions are not actually complicated — they are{" "}
      <strong>crowded</strong>. Crowded with unexamined assumptions, unnamed pressure, and a push
      to decide faster than the situation requires. Strip those out, and most decisions become much
      simpler than they felt.
    </p>
    <GuideSectionLabel>A simple process, using EIER</GuideSectionLabel>
    <p className="guide-panel__p">
      The EIER framework — <strong>Event → Interpretation → Evidence → Response</strong> — gives
      you a sequence to move through instead of a fog to push against.
    </p>
    <GuideBlockList
      items={[
        "1. Name the Event. What actually happened — the observable version.",
        "2. Separate the Interpretation. What story am I adding? Is it the only possible one?",
        "3. Check the Evidence. What supports this read, and what challenges it?",
        "4. Choose the Response. Given 1–3 clearly seen, what actually makes sense to do?",
      ]}
    />
    <GuideCallout>
      Most fog lives in step 2 being mistaken for step 1, before Evidence has been checked.
      Separate them, and the decision often becomes obvious.
    </GuideCallout>
    <GuideSectionLabel>Making this practical</GuideSectionLabel>
    <GuideCardGrid columns={2}>
      <GuideCard
        iconKey="awareness"
        title="Slow Interpretation, not the whole process"
        description="You can decide quickly once the fog is cleared — the delay belongs at Interpretation, not across every step."
      />
      <GuideCard
        iconKey="thinking"
        title="Write it out when stakes are high"
        description="Thinking in loops keeps Event and Interpretation tangled. Writing forces separation."
      />
      <GuideCard
        iconKey="communication"
        title="Ask what you would tell a friend"
        description="You apply this process naturally for other people. The skill is turning it on yourself."
      />
      <GuideCard
        iconKey="growth"
        title='Accept "good enough" clarity'
        description="You do not need certainty — you need enough clarity to commit and move."
      />
    </GuideCardGrid>
    <GuideCallout variant="amber">
      Clear decision making is not a talent some people have and others lack. It is a sequence.
      Anyone can run it.
    </GuideCallout>
  </GuidePanel>
);

export default ClearDecisionMakingSection;
